import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state } from 'lit/decorators.js';
import { RTL_LANGUAGE, CUSTOM_FONT_URLS } from '../utils/constant';
import cookieConfigManager from '../utils/cookie-consent-manager';
import { readUserConfig } from '../utils/config';
import { monitorSelfErrors, teaReport } from '$tea';
import { showToast, loadCustomFonts } from '../utils/ui';
import { NavigationInfos, CookieConsent } from '../interface/cookie-banner';
import styles from './cookie-banner.scss';
import './setting-modal';
import consentMode from '../utils/consent-mode';

CUSTOM_FONT_URLS.forEach(([fontName, fontUrl]) => loadCustomFonts(fontName, fontUrl));

monitorSelfErrors();

@customElement('tiktok-cookie-banner')
export class CookieBanner extends LitElement {
  static override styles = unsafeCSS(styles);

  @property()
  locale!: string;
  @property({
    attribute: 'user-config-ele-id',
  })
  userConfigEleId!: string;
  @property({
    converter: (value: string | null) => 'true' === value,
  })
  disabled = false;

  @state() i18n: Record<string, string> = {};
  @state() userConfig?: NavigationInfos;
  @state() cookieConsent?: CookieConsent;
  @state() disabledByPolicy = false;
  @state() settingVisible = false;
  @state() forceUpdater = 1;

  private region = '';
  private isFTC = false;
  private previousShow = false;

  get showCookieBanner(): boolean {
    const show =
      !this.disabled &&
      !this.disabledByPolicy &&
      cookieConfigManager.isShowCookieBanner({
        region: this.region,
        isFTC: this.isFTC,
      });
    if (show !== this.previousShow) {
      if (show) {
        teaReport('cookie_banner_show');
      }
      this.sendEvent('bannerEvents', {
        type: show ? 'show' : 'hide',
      });
      this.previousShow = show;
    }
    return show;
  }

  /**
   * used by cookie policy page only
   */
  async reload() {
    this.requestUpdate();
  }

  override connectedCallback() {
    super.connectedCallback();
    teaReport('cookie_banner_load');
    try {
      const userConfig = readUserConfig(this.userConfigEleId, this.locale);
      if (!userConfig) {
        return;
      }
      const { i18n, region, isFTC, disabled: disabledByPolicy } = userConfig;
      Object.assign(this, {
        i18n,
        region,
        isFTC,
        disabledByPolicy,
      });
      const cookieConsent = cookieConfigManager.getCookieConsents({ region, isFTC });
      consentMode.initializeWithConsent(cookieConsent);
      this.execConsentsGetCallback(cookieConsent);
      this.cookieConsent = cookieConsent;
    } catch (e) {
      console.error(e);
    }
  }

  override updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('locale')) {
      const userConfig = readUserConfig(this.userConfigEleId, this.locale);
      if (userConfig?.i18n) {
        this.i18n = userConfig.i18n;
      }
    }
  }

  private execConsentsGetCallback(cookieConsent: CookieConsent) {
    this.sendEvent('bannerEvents', {
      type: 'consentGet',
      payload: [
        cookieConsent,
        {
          region: this.region,
          isFTC: this.isFTC,
        },
      ],
    });
  }

  private handleDescClick(ev: Event) {
    const { target } = ev;
    if (target instanceof HTMLAnchorElement) {
      const { href } = target;
      if (!href) {
        return;
      }
      if (href.includes('/legal/tiktok-website-cookies-policy')) {
        teaReport('cookie_banner_go_to_policy');
      } else if (href.includes('/legal/cookie-settings')) {
        // use settings modal instead
        ev.preventDefault();
        this.settingVisible = true;
        teaReport('cookie_banner_go_to_settings');
      }
    }
  }

  private handleConfirm() {
    const { i18n } = this;
    const { webDoNotSellSettingsSavedToast } = i18n;
    const cookieConsent = cookieConfigManager.setFullConsents();
    consentMode.updateWithConsent(cookieConsent);
    this.execConsentsGetCallback(cookieConsent);
    this.sendEvent('bannerEvents', {
      type: 'accept',
    });
    this.cookieConsent = cookieConsent;
    showToast(webDoNotSellSettingsSavedToast);
    teaReport('cookie_banner_accept');
  }

  private handleDecline() {
    const { i18n } = this;
    const { webDoNotSellSettingsSavedToast } = i18n;
    const cookieConsent = cookieConfigManager.setEmptyConsents();
    consentMode.updateWithConsent(cookieConsent);
    this.execConsentsGetCallback(cookieConsent);
    this.sendEvent('bannerEvents', {
      type: 'decline',
    });
    this.cookieConsent = cookieConsent;
    showToast(webDoNotSellSettingsSavedToast);
    teaReport('cookie_banner_decline');
  }

  private handleSave(ev: CustomEvent<CookieConsent>) {
    const { detail } = ev;
    const { i18n } = this;
    const { webDoNotSellSettingsSavedToast } = i18n;
    consentMode.updateWithConsent(detail);
    this.execConsentsGetCallback(detail);
    this.sendEvent('bannerEvents', {
      type: 'save',
      payload: [detail],
    });
    this.cookieConsent = detail;
    showToast(webDoNotSellSettingsSavedToast);
  }

  private sendEvent<T>(eventName: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent<T>(eventName, {
        detail,
      }),
    );
  }

  override render() {
    const { locale = 'en', i18n } = this;
    const { cookieBannerTitleNew, cookieBannerSubTitleV2, cookieBannerBtnDecline, cookieBannerBtnAccept } = i18n;

    const cookieBanner = this.showCookieBanner
      ? html`
          <div
            class=${classMap({
              'tiktok-cookie-banner': true,
              'is-rtl': RTL_LANGUAGE.includes(locale),
            })}
          >
            <div class="cookie-content">
              <p class="cookie-title">${cookieBannerTitleNew}</p>
              <p class="cookie-desc" @click=${this.handleDescClick} .innerHTML=${cookieBannerSubTitleV2}></p>
            </div>
            <div class="button-wrapper">
              <button type="button" @click=${this.handleDecline}>${cookieBannerBtnDecline}</button>
              <button type="button" @click=${this.handleConfirm}>${cookieBannerBtnAccept}</button>
            </div>
          </div>
        `
      : nothing;
    return html`
      ${cookieBanner}
      <cookie-setting-modal
        .visible=${this.settingVisible}
        .userConfigEleId=${this.userConfigEleId}
        .locale=${this.locale}
        @close=${() => (this.settingVisible = false)}
        @save=${this.handleSave}
      >
      </cookie-setting-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cookie-banner': CookieBanner;
  }
}
